/**
 * Einstellungen des Schulträgers. Den Verbindungscode sehen alle Träger-User; Name,
 * neuer Code und Löschen nur der Haupt-Trägeradmin.
 */
import { api } from "../api";
import { openModal, closeModal, modalBody } from "../components/modal";
import { confirmDialog } from "../components/confirm-dialog";
import { showToast } from "../utils/toast";
import { switchCarrierView } from "../utils/carrier-view";
import type { CarrierInfo } from "../types";
import { esc, formatDate } from "./traeger-shared";

let info: CarrierInfo | null = null;

export async function render(el: HTMLElement) {
  info = await api.carrier.get();
  const founder = info.isFounder;
  el.innerHTML = `
    <div class="page-header"><h1>Einstellungen</h1></div>

    <div class="card card-body" style="margin-bottom:1.25rem">
      <h3 style="font-size:.9375rem;font-weight:600;margin-bottom:.25rem">Code zum Verbinden</h3>
      <p class="text-muted" style="font-size:.875rem;margin:0 0 .75rem">
        Eine Schule gibt diesen Code unter Einstellungen, Allgemein ein und stellt damit eine Anfrage.
        Verbunden ist sie erst, wenn ${founder ? "du ihre Anfrage unter Schulen annimmst" : "der Haupt-Trägeradmin ihre Anfrage annimmt"}.
      </p>
      <div style="display:flex;align-items:center;gap:.75rem;flex-wrap:wrap">
        <span class="carrier-code" id="tr-code">${esc(info.code)}</span>
        <button class="btn btn-sm" id="tr-copy">Kopieren</button>
        ${founder ? `<button class="btn btn-sm" id="tr-new-code">Neuen Code erzeugen</button>` : ""}
      </div>
    </div>

    <div class="card card-body" style="margin-bottom:1.25rem">
      <h3 style="font-size:.9375rem;font-weight:600;margin-bottom:.75rem">Schulträger</h3>
      <div class="form-grid">
        <div class="form-group"><label>Name</label><input id="tr-name" value="${esc(info.name)}" ${founder ? "" : "disabled"} /></div>
        <div class="form-group"><label>Stammschule</label><input value="${esc(info.foundingSchool.name)}" disabled /></div>
        <div class="form-group"><label>Haupt-Trägeradmin</label><input value="${esc(info.founder ? `${info.founder.name} (${info.founder.email})` : "nicht gefunden")}" disabled /></div>
        <div class="form-group"><label>Erstellt am</label><input value="${esc(formatDate(info.createdAt))}" disabled /></div>
      </div>
      ${founder ? `<div class="form-actions" style="margin-top:.75rem"><button class="btn btn-primary" id="tr-save">Speichern</button></div>` : ""}
    </div>

    ${founder ? `
    <div class="card card-body" style="border-color:var(--red-200,#fecaca)">
      <h3 style="font-size:.9375rem;font-weight:600;margin-bottom:.25rem">Schulträger löschen</h3>
      <p class="text-muted" style="font-size:.875rem;margin:0 0 .75rem">
        Alle Schulen werden vom Schulträger getrennt und bleiben mit ihren Daten erhalten. Träger-User verlieren ihren Zugang.
        Erst danach lassen sich die Stammschule und dein Benutzerkonto löschen.
      </p>
      <button class="btn btn-danger-fill" id="tr-delete">Schulträger löschen</button>
    </div>` : ""}`;

  el.querySelector("#tr-copy")!.addEventListener("click", async () => {
    try { await navigator.clipboard.writeText(info!.code); showToast("Code kopiert"); }
    catch { showToast("Kopieren nicht möglich"); }
  });
  el.querySelector("#tr-new-code")?.addEventListener("click", async () => {
    const ok = await confirmDialog({
      title: "Neuen Code erzeugen",
      message: "Der bisherige Code funktioniert danach nicht mehr. Bereits verbundene Schulen bleiben verbunden.",
      confirmLabel: "Neuen Code erzeugen",
    });
    if (!ok) return;
    try {
      const { code } = await api.carrier.regenerateCode();
      info!.code = code;
      el.querySelector("#tr-code")!.textContent = code;
      showToast("Neuer Code erzeugt");
    } catch (err: any) { alert("Fehler: " + err.message); }
  });
  el.querySelector("#tr-save")?.addEventListener("click", async () => {
    const name = (el.querySelector("#tr-name") as HTMLInputElement).value.trim();
    if (!name) { alert("Bitte einen Namen eingeben."); return; }
    try { await api.carrier.update(name); showToast("Gespeichert"); }
    catch (err: any) { alert("Fehler: " + err.message); }
  });
  el.querySelector("#tr-delete")?.addEventListener("click", openDelete);
}

function openDelete() {
  openModal("Schulträger löschen", `
    <p style="font-size:.875rem;color:var(--gray-700)">
      Der Schulträger „${esc(info!.name)}“ wird gelöscht. Alle Schulen werden getrennt und bleiben erhalten.
      Bitte bestätige mit deinem Passwort.
    </p>
    <div class="form-group"><label>Passwort *</label><input id="td-pw" type="password" autocomplete="current-password" /></div>
    <div id="td-error" style="color:var(--danger,#dc2626);font-size:.875rem;min-height:1.2rem"></div>
    <div class="form-actions"><button class="btn btn-secondary" id="td-cancel">Abbrechen</button><button class="btn btn-danger-fill" id="td-ok">Endgültig löschen</button></div>`);
  const mb = modalBody();
  mb.querySelector("#td-cancel")!.addEventListener("click", closeModal);
  mb.querySelector("#td-ok")!.addEventListener("click", async () => {
    const pw = (mb.querySelector("#td-pw") as HTMLInputElement).value;
    const errorEl = mb.querySelector("#td-error") as HTMLElement;
    if (!pw) { errorEl.textContent = "Bitte Passwort eingeben."; return; }
    try {
      await api.carrier.delete(pw);
      closeModal();
      // Ohne Träger gibt es nur noch die eigene Schule
      switchCarrierView({ mode: "eigene" }, "#/dashboard");
    } catch (err: any) { errorEl.textContent = err.message; }
  });
}
